import React, { useEffect, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faEdit,
  faEye,
  faPlus,
  faSearch,
  faTrash,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast } from "react-hot-toast";
import moment from "moment";
import { generateConfirm } from "../../helpers";
import useScrollTop from "../../hooks/useScrollTop";
import usePermissionStatus from "../../hooks/usePermissionStatus";
import { getNews, updateStatus } from "../../services/news.js";

const admin_type = localStorage.getItem("admin_type") || "";
const NewsList = () => {
  const navigate = useNavigate();
  const [searchNews, setSearchNews] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const permit = usePermissionStatus(["add_news", "edit_news", "delete_news"]);

  const [payload, setPayload] = useState({
    page: 1,
    limit: 10,
    search_key: "",
  });

  const [newsLists, setNewsLists] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const pageCount = Math.ceil(totalCount / payload.limit);
  useScrollTop();

  useEffect(() => {
    localStorage.removeItem("current_news_id");
    localStorage.removeItem("current_news_details");
    fetchNewsList();
  }, [payload]);

  const fetchNewsList = async () => {
    setIsLoading(true);
    try {
      const response = await getNews(payload);
      if (response?.data?.statusCode === 200) {
        // Map the API response to match your component's expected format
        const mappedNews = response.data.result.map((news) => ({
          news_id: news.news_id,
          news_title: news.title,
          category_name: news.category_name || news.category,
          published_at: news.created_at,
          is_popular: news.is_popular,
          is_featured: news.is_featured,
          status: news.status || "1", // Default to active if not provided
          // Add any other fields your component expects
        }));

        setNewsLists(mappedNews);
        setTotalCount(response.data.total_count || response.data.total || 0);
      } else {
        toast.error("Failed to fetch news");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error fetching news");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageClick = (e) => {
    setPayload({ ...payload, page: e.selected + 1 });
  };

  const handleSearch = () => {
    setPayload({ ...payload, search_key: searchNews, page: 1 });
  };

  const handleUpdateStatus = async (newsId, type, value) => {
    try {
      const updatePayload = {
        news_id: String(newsId),
        type: type,
        value: value,
      };

      const response = await updateStatus(updatePayload);

      if (response?.data?.statusCode === 200) {
        toast.success(
          response?.data?.statusMessage || "News updated successfully"
        );
        fetchNewsList(); // Refresh the list
      } else {
        toast.error("Failed to update news status");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error updating news status");
    }
  };

  const handleToggleStatus = (newsId, currentStatus) => {
    const newStatus = currentStatus === "1" ? "0" : "1"; // Toggle between active and inactive
    const statusText = newStatus === "1" ? "active" : "inactive";

    generateConfirm(
      `Are you sure you want to mark this news as ${statusText}?`,
      `Press Yes if you want to change the status to ${statusText}.`,
      "news-status-toggle",
      () => handleUpdateStatus(newsId, "status", newStatus)
    );
  };

  const handleTogglePopular = (newsId, currentStatus) => {
    const newStatus = currentStatus === "1" ? "0" : "1"; // Toggle between popular and not popular
    const statusText = newStatus === "1" ? "popular" : "not popular";

    generateConfirm(
      `Are you sure you want to mark this news as ${statusText}?`,
      `Press Yes if you want to change the popular status.`,
      "news-popular-toggle",
      () => handleUpdateStatus(newsId, "is_popular", newStatus)
    );
  };

  const handleToggleFeatured = (newsId, currentStatus) => {
    const newStatus = currentStatus === "1" ? "0" : "1";
    const statusText = newStatus === "1" ? "featured" : "not featured";

    generateConfirm(
      `Are you sure you want to mark this news as ${statusText}?`,
      `Press Yes if you want to change the featured status.`,
      "news-featured-toggle",
      () => handleUpdateStatus(newsId, "is_featured", newStatus)
    );
  };

  const handleDeleteNews = (newsId) => {
    generateConfirm(
      "Are you sure you want to Delete News?",
      "Press Yes if you want to delete",
      "news-delete",
      () => handleUpdateStatus(newsId, "status", "2") // 2 for delete
    );
  };

  return (
    <div className="dashboard news-list subadmin-list">
      <div className="subheader mb-3">
        <Row>
          <Col xs={5}>
            <h2 className="heading-36 ">News List</h2>
          </Col>
          <Col xs={7}>
            <div className="subheader-right">
              <div className="subheader-right-top">
                <Form.Group controlId="formFile" className="subheader-search">
                  <Form.Control
                    type="search"
                    placeholder="search here"
                    onChange={(e) => setSearchNews(e.target.value)}
                    value={searchNews}
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="search-icon"
                    onClick={handleSearch}
                  />
                </Form.Group>
                <button
                  onClick={() => navigate("/add-news")}
                  // className={`${permit ? "" : "disabledbutton"} add-btn`}
                  className="add-btn"
                >
                  <FontAwesomeIcon icon={faPlus} /> Add New News
                </button>
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <div className="table-main fixTableHead">
        {!isLoading ? (
          newsLists.length > 0 ? (
            <table className="table nowrap table-bordered table-default-dark">
              <thead>
                <tr className="table-heading">
                  <th>S.No</th>
                  <th>News Title</th>
                  <th>Category</th>
                  <th>Published On</th>
                  <th className="text-center">Status</th>
                  <th>Popular</th>
                  <th>Featured</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {newsLists.map((news, index) => (
                  <tr key={news.news_id}>
                    <td>{index + 1}</td>
                    <td className="truncate-title" title={news.news_title}>
                      {news.news_title.length > 50
                        ? news.news_title.substring(0, 50) + "..."
                        : news.news_title}
                    </td>
                    <td>{news.category_name}</td>
                    <td>{moment(news.published_at).format("DD/MM/YYYY")}</td>
                    <td>
                      <div
                        className={` status-toggle d-inline-block ${
                          news.status === "1" ? "active" : "inactive"
                        }`}
                        onClick={() =>
                          handleToggleStatus(news.news_id, news.status)
                        }
                      >
                        <div className="toggle-switch">
                          <div className="toggle-knob"></div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="form-check justify-content-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={news.is_popular === "1"}
                          onChange={() =>
                            handleTogglePopular(news.news_id, news.is_popular)
                          }
                        />
                      </div>
                    </td>
                    <td>
                      <div className="form-check justify-content-center">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={news.is_featured === "1"}
                          onChange={() =>
                            handleToggleFeatured(news.news_id, news.is_featured)
                          }
                        />
                      </div>
                    </td>
                    <td>
                      <Button
                        className="edit-btn me-1"
                        onClick={() => navigate(`/edit-news/${news.news_id}`)}
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button
                        className="view-btn me-1"
                        onClick={() => navigate(`/view-news/${news.news_id}`)}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                      <Button className="delete-btn me-1">
                        <FontAwesomeIcon
                          icon={faTrash}
                          onClick={() => handleDeleteNews(news.news_id)}
                        />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <h1 className="no-data">No Record Found</h1>
          )
        ) : (
          <h1 className="no-data">Loading...</h1>
        )}
      </div>

      {totalCount > payload.limit && (
        <ReactPaginate
          previousLabel={<FontAwesomeIcon icon={faAngleDoubleLeft} />}
          nextLabel={<FontAwesomeIcon icon={faAngleDoubleRight} />}
          breakLabel={"..."}
          pageCount={pageCount}
          marginPagesDisplayed={2}
          pageRangeDisplayed={5}
          onPageChange={handlePageClick}
          containerClassName={"pagination custom-pagination"}
          activeClassName={"active"}
        />
      )}
    </div>
  );
};

export default NewsList;
